import React, { useEffect, useState } from "react";
import axios from "axios";
import { DATA_SAVE_URL } from "../utils/UrlHandle";
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Download, PictureAsPdf, Visibility } from "@mui/icons-material";

const PDFViewerIncomeExpense = ({ date, reportNo, year, month, monthName }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFilename, setPdfFilename] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAndroid = /Android/i.test(navigator.userAgent);

  const generateFilename = () => {
    return `lab-kekirawa-${reportNo}.pdf`;
  };

  useEffect(() => {
    setTimeout(() => {
      fetchPDF("view");
    }, 1000);
  }, []);

  const fetchPDF = async (action = "view") => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        DATA_SAVE_URL + "/api/v1/reports/categorySumReport/" + 1,
        {
          responseType: "blob",
          params: {
            year: year,
            month: month,
            monthName: monthName,
          },
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const filename = generateFilename();
      const url = URL.createObjectURL(blob);

      // For Android, always download or open in new tab
      if (isAndroid || action === "download") {
        if (isAndroid && action === "view") {
          // Open in new tab for Android view
          window.open(url, "_blank");
        } else {
          // Download for both Android and other devices
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        URL.revokeObjectURL(url);
      } else {
        // For non-Android devices, show in iframe
        setPdfUrl(url);
        setPdfFilename(filename);
      }
    } catch (error) {
      console.error("Error fetching PDF:", error);
      setError("Failed to load PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Alternative: Direct download for Android when viewing fails
  const handleAndroidView = () => {
    if (isAndroid && pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, "_blank");
    }
  };

  return (
    <Box
      sx={{
        p: isSmallMobile ? 1 : 2,
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* Control Buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallMobile ? "column" : "row",
          gap: isSmallMobile ? 1 : 2,
          mb: 2,
          alignItems: isSmallMobile ? "stretch" : "flex-start",
        }}
      >
        {/* <Button
          onClick={() => fetchPDF("view")}
          disabled={loading}
          size={isSmallMobile ? "small" : "medium"}
          color="info"
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <Visibility />}
          fullWidth={isSmallMobile}
          sx={{
            minWidth: isSmallMobile ? "auto" : "120px",
          }}
        >
          {loading ? "Loading..." : "View PDF"}
        </Button> */}

        <Button
          onClick={() => fetchPDF("download")}
          disabled={loading}
          size={isSmallMobile ? "small" : "medium"}
          color="info"
          variant="contained"
          startIcon={<Download />}
          fullWidth={isSmallMobile}
          sx={{
            minWidth: isSmallMobile ? "auto" : "140px",
          }}
        >
          Download PDF
        </Button>

        {/* Additional button for Android to open in new tab */}
        {isAndroid && pdfUrl && (
          <Button
            onClick={handleAndroidView}
            size={isSmallMobile ? "small" : "medium"}
            color="secondary"
            variant="outlined"
            fullWidth={isSmallMobile}
            sx={{
              minWidth: isSmallMobile ? "auto" : "160px",
            }}
          >
            Open in New Tab
          </Button>
        )}
      </Box>

      {/* Error Message */}
      {error && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: "error.light",
            color: "error.contrastText",
            borderRadius: 1,
            textAlign: "center",
          }}
        >
          <Typography variant="body2">{error}</Typography>
        </Box>
      )}

      {/* Android-specific warning */}
      {isAndroid && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            backgroundColor: "warning.light",
            color: "warning.contrastText",
            borderRadius: 1,
            textAlign: "center",
          }}
        >
          <Typography variant="caption">
            Android devices may have limited PDF preview support. Use "Open in
            New Tab" or download the PDF.
          </Typography>
        </Box>
      )}

      {/* PDF Info */}
      {pdfUrl && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: "grey.50",
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
            <strong>PDF File:</strong> {pdfFilename}
          </Typography>
        </Box>
      )}

      {/* PDF Viewer */}
      {pdfUrl && (
        <Box
          sx={{
            width: "100%",
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 1,
            overflow: "hidden",
            backgroundColor: "white",
          }}
        >
          <iframe
            src={pdfUrl}
            width="100%"
            height={isMobile ? "500px" : "800px"}
            title={pdfFilename}
            style={{
              border: "none",
              display: "block",
            }}
            onError={(e) => {
              console.error("PDF loading failed");
              if (isAndroid && pdfBlob) {
                // Fallback: open in new tab
                const url = URL.createObjectURL(pdfBlob);
                window.open(url, "_blank");
              }
            }}
          />
        </Box>
      )}

      {/* Loading State */}
      {loading && !pdfUrl && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading PDF document...
          </Typography>
        </Box>
      )}

      {/* No PDF State */}
      {!loading && !pdfUrl && !error && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            flexDirection: "column",
            gap: 2,
            backgroundColor: "grey.50",
            borderRadius: 1,
            border: "1px dashed",
            borderColor: "grey.300",
          }}
        >
          <PictureAsPdf sx={{ fontSize: 48, color: "grey.400" }} />
          <Typography variant="body1" color="text.secondary" align="center">
            Click "View PDF" to load the document
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PDFViewerIncomeExpense;
